/**
 * The language the UI renders in, and the loaded catalogues.
 *
 * Deliberately free of stores and Tauri: the app-language store decides which
 * language the user wants and calls `activateLanguage`; this module loads that
 * language's catalogues and only then switches, so a change never paints a
 * half-English frame. React reads it through `useT` (`useSyncExternalStore`),
 * everything else through `t`.
 *
 * English lives in the bundle (every `en.ts` is imported by the area that uses
 * it); German and Chinese each load as one chunk built with
 * `import.meta.glob`, so a new area registers itself without a shared list.
 */
import { pseudoLocalize } from "./pseudo";
import type {
  AppLanguage,
  MessageCatalogue,
  MessageValue,
  PluralForms,
} from "./types";

type Translated = Exclude<AppLanguage, "en">;

/** One chunk per language (see catalogues-de.ts): a window waits for one
 *  request, not one per area, and a new area registers itself. */
const LOADERS: Record<
  Translated,
  () => Promise<{ default: Record<string, MessageCatalogue> }>
> = {
  de: () => import("./catalogues-de"),
  zh: () => import("./catalogues-zh"),
};

const PSEUDO_STORAGE_KEY = "denker.i18n.pseudo";

const loaded: Partial<Record<Translated, Record<string, MessageCatalogue>>> =
  {};
let active: AppLanguage = "en";
let pseudo = false;
let requested: AppLanguage = "en";
const listeners = new Set<() => void>();

/** Dev builds only: `localStorage["denker.i18n.pseudo"] = "1"` renders every
 *  English message accented and ~40% longer, to find clipping and raw text. */
function pseudoRequested(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    return globalThis.localStorage?.getItem(PSEUDO_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

async function loadLanguage(language: Translated): Promise<void> {
  if (loaded[language]) return;
  const modules = (await LOADERS[language]()).default;
  const byArea: Record<string, MessageCatalogue> = {};
  for (const [path, catalogue] of Object.entries(modules)) {
    const area = /\.\/messages\/([^/]+)\//.exec(path)?.[1];
    if (area) byArea[area] = catalogue;
  }
  loaded[language] = byArea;
}

/** Load `language` and make it the rendered one. The latest call wins when
 *  two overlap. Resolves once the UI shows it. */
export async function activateLanguage(language: AppLanguage): Promise<void> {
  requested = language;
  if (language !== "en") await loadLanguage(language);
  if (requested !== language) return;
  const nextPseudo = language === "en" && pseudoRequested();
  if (language === active && nextPseudo === pseudo) return;
  active = language;
  pseudo = nextPseudo;
  if (typeof document !== "undefined")
    document.documentElement.lang = LOCALE_TAGS[language];
  listeners.forEach((listener) => listener());
}

/** The BCP-47 tag of each app language: `<html lang>`, `Intl` and dictation. */
export const LOCALE_TAGS: Record<AppLanguage, string> = {
  en: "en-US",
  de: "de-DE",
  zh: "zh-CN",
};

export function activeLanguage(): AppLanguage {
  return active;
}

export function activeLocaleTag(): string {
  return LOCALE_TAGS[active];
}

export function subscribeLanguage(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The text for `key` in the rendered language; English when the language
 *  has no catalogue for this area (never happens once the area is typed). */
export function lookupMessage(
  area: string,
  en: MessageCatalogue,
  key: string,
): MessageValue | undefined {
  const value =
    active === "en" ? en[key] : (loaded[active]?.[area]?.[key] ?? en[key]);
  if (value === undefined) return undefined;
  if (!pseudo) return value;
  return typeof value === "string"
    ? pseudoLocalize(value)
    : (Object.fromEntries(
        Object.entries(value).map(([form, text]) => [
          form,
          pseudoLocalize(text as string),
        ]),
      ) as PluralForms);
}
